import React, { useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { submissionsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { CheckCircle, Loader2 } from 'lucide-react';

const submissionTypes = [
  { value: 'story_pitch', label: 'Story Pitch' },
  { value: 'full_article', label: 'Full Article' },
  { value: 'photo_set', label: 'Photo Set' },
  { value: 'video', label: 'Video' },
  { value: 'tip', label: 'Tip' },
];

const categories = [
  { value: 'Film', label: 'Film' },
  { value: 'Music', label: 'Music' },
  { value: 'TV', label: 'TV' },
  { value: 'Sports', label: 'Sports' },
  { value: 'Business', label: 'Business' },
];

export const SubmitPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    social_handles: '',
    submission_type: '',
    category: '',
    title: '',
    summary: '',
    content: '',
    video_link: '',
    rights_confirmed: false,
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.rights_confirmed) {
      toast.error('Please confirm you have rights to submit this content');
      return;
    }

    setLoading(true);
    try {
      await submissionsAPI.create({
        ...formData,
        images: [],
      });
      setSubmitted(true);
      toast.success('Submission received! Our team will review it shortly.');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="container-custom py-24">
          <div className="max-w-xl mx-auto text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
            <h1 className="font-serif text-3xl md:text-4xl font-bold mb-4">
              Submission Received!
            </h1>
            <p className="text-muted-foreground text-lg mb-8">
              Thank you for your submission. Our editorial team will review it and get back to you if we'd like to publish it.
            </p>
            <Button onClick={() => {
              setSubmitted(false);
              setFormData({
                name: '',
                email: '',
                phone: '',
                social_handles: '',
                submission_type: '',
                category: '',
                title: '',
                summary: '',
                content: '',
                video_link: '',
                rights_confirmed: false,
              });
            }} data-testid="submit-another-btn">
              Submit Another
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-foreground text-background py-16 md:py-24">
        <div className="container-custom text-center">
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            Submit Your Content
          </h1>
          <p className="text-background/70 text-lg max-w-2xl mx-auto">
            Have a story, photos, or video to share? Submit your content for review by our editorial team.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-12 md:py-16">
        <div className="container-custom max-w-2xl">
          <form onSubmit={handleSubmit} className="space-y-8" data-testid="submission-form">
            {/* Contact Info */}
            <div className="space-y-6">
              <h2 className="font-serif text-2xl font-bold pb-4 border-b border-border">
                Your Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="submit-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="submit-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone (optional)</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    data-testid="submit-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="social_handles">Social Handles (optional)</Label>
                  <Input
                    id="social_handles"
                    name="social_handles"
                    placeholder="@yourhandle"
                    value={formData.social_handles}
                    onChange={handleChange}
                    data-testid="submit-social"
                  />
                </div>
              </div>
            </div>

            {/* Submission Details */}
            <div className="space-y-6">
              <h2 className="font-serif text-2xl font-bold pb-4 border-b border-border">
                Submission Details
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Submission Type *</Label>
                  <Select
                    value={formData.submission_type}
                    onValueChange={(value) => handleSelectChange('submission_type', value)}
                    required
                  >
                    <SelectTrigger data-testid="submit-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {submissionTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => handleSelectChange('category', value)}
                    required
                  >
                    <SelectTrigger data-testid="submit-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Title / Headline *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  data-testid="submit-title"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="summary">Short Summary *</Label>
                <Textarea
                  id="summary"
                  name="summary"
                  rows={3}
                  value={formData.summary}
                  onChange={handleChange}
                  required
                  placeholder="Brief description of your submission..."
                  data-testid="submit-summary"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="content">Full Content (optional)</Label>
                <Textarea
                  id="content"
                  name="content"
                  rows={8}
                  value={formData.content}
                  onChange={handleChange}
                  placeholder="Paste your full article or story here..."
                  data-testid="submit-content"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="video_link">Video Link (optional)</Label>
                <Input
                  id="video_link"
                  name="video_link"
                  type="url"
                  value={formData.video_link}
                  onChange={handleChange}
                  placeholder="https://youtube.com/watch?v=..."
                  data-testid="submit-video-link"
                />
              </div>
            </div>

            {/* Rights Confirmation */}
            <div className="flex items-start gap-3 p-4 bg-secondary/50 rounded-sm">
              <Checkbox
                id="rights_confirmed"
                checked={formData.rights_confirmed}
                onCheckedChange={(checked) => 
                  setFormData(prev => ({ ...prev, rights_confirmed: checked }))
                }
                data-testid="submit-rights-checkbox"
              />
              <Label htmlFor="rights_confirmed" className="text-sm leading-relaxed cursor-pointer">
                I confirm I own the rights or have permission to submit this content. I understand that by submitting, I grant The Trade Media the right to publish this content with attribution.
              </Label>
            </div>

            {/* Submit Button */}
            <Button 
              type="submit" 
              size="lg" 
              className="w-full"
              disabled={loading}
              data-testid="submit-form-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit for Review'
              )}
            </Button>
          </form>
        </div>
      </section>
    </Layout>
  );
};

export default SubmitPage;
